import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { Container, Row, Col } from 'react-bootstrap';
import axios from "axios";

import constants from "../../const";
import AlbumSidebar from "./album-sidebar";
import MoveImageModal from "./move-image-modal";
import ImageInfoModal from "./image-info-modal";
import FullscreenAdminPanel from "./fullscreen-admin-panel";
import { helper } from "../common/helper";
import ImagePositionTagger from "./image-position-tagger";
import styled, { css } from "styled-components";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditIcon from "@mui/icons-material/Edit";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import useWindowSize from "../common/useWindowSize";
import { trackSearch } from "../../controller/viewTracker";

const SIDEBAR_MIN_WIDTH = 180;

// Icon-only circular buttons, styled to match the chat launcher
const FloatingAdminButton = styled.button`
	width: 44px;
	height: 44px;
	border-radius: 50%;
	border: 2px solid #667eea;
	background: white;
	color: #667eea;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
	transition: all 0.3s ease;

	${({ $active }) => $active && css`
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		color: white;
	`}

	&:hover {
		transform: scale(1.1);
	}

	&:active {
		transform: scale(0.95);
	}
`;

// Compact per-image action menu (replaces the row of circular buttons)
const ToolbarMenuButton = styled.button`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background: rgba(0, 0, 0, 0.65);
	color: white;
	font-size: 18px;
	line-height: 1;
	padding: 0;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

	&:hover {
		background: rgba(0, 0, 0, 0.85);
	}
`;

const ToolbarMenuPanel = styled.div`
	position: absolute;
	top: 34px;
	right: 0;
	z-index: 20;
	min-width: 180px;
	display: flex;
	flex-direction: column;
	padding: 4px;
	background: white;
	border: 1px solid #ddd;
	border-radius: 8px;
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
`;

const ToolbarMenuItem = styled.button`
	padding: 6px 10px;
	border: none;
	border-radius: 6px;
	background: none;
	text-align: left;
	font-size: 13px;
	white-space: nowrap;
	color: ${({ $danger }) => ($danger ? '#cb2d3e' : '#333')};
	cursor: pointer;

	&:hover {
		background: ${({ $danger }) => ($danger ? '#fdecea' : '#f0f2ff')};
	}
`;

export default function SimpleGallery(props) {

	var token = `?token=${localStorage.getItem('token').slice(7)}`;
	const [album, setAlbum] = "Pictures"
	const [pics, setPics] = useState({ contents: [], children: {} });
	const [galleryData, setGalleryData] = useState({ contents: [], children: {} });
	const [readyToRender, setReadyToRender] = useState(false)
	const [displayedImages, setDisplayedImages] = useState([]);
	const [imagesPerPage] = useState(20);
	const [loadedCount, setLoadedCount] = useState(0);
	const sentinelRef = React.useRef(null);
	const [selectedImageForTagging, setSelectedImageForTagging] = useState(null);
	const [imagePositions, setImagePositions] = useState({});
	const [hoveredImageKey, setHoveredImageKey] = useState(null);
	const [sidebarWidth, setSidebarWidth] = useState(() => {
		const saved = Number(localStorage.getItem('gallerySidebarWidth'));
		return Number.isFinite(saved) && saved >= SIDEBAR_MIN_WIDTH ? saved : 250;
	});
	useEffect(() => {
		localStorage.setItem('gallerySidebarWidth', String(sidebarWidth));
	}, [sidebarWidth]);
	const { width: viewportWidth } = useWindowSize();
	const isSidebarBesideGallery = props.displayAlbum && viewportWidth >= 768;

	const startSidebarResize = (e) => {
		e.preventDefault();
		const startX = e.clientX;
		const startWidth = sidebarWidth;
		const onMove = (moveEvent) => {
			setSidebarWidth(Math.max(SIDEBAR_MIN_WIDTH, startWidth + moveEvent.clientX - startX));
		};
		const onUp = () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
		};
		document.body.style.userSelect = 'none';
		document.body.style.cursor = 'col-resize';
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
	};
	const [isAdmin, setIsAdmin] = useState(false);
	const [multiSelectMode, setMultiSelectMode] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [openMenuKey, setOpenMenuKey] = useState(null);
	const [movingImageName, setMovingImageName] = useState(null);
	const [floatingCollapsed, setFloatingCollapsed] = useState(false);
	const [fullscreenImage, setFullscreenImage] = useState(null);
	const [fullscreenPanelOpen, setFullscreenPanelOpen] = useState(true);
	const lightboxRef = React.useRef(null);
	const [selectedImages, setSelectedImages] = useState(new Set());
	const gridRef = React.useRef(null);
	const suppressClickRef = React.useRef(false);
	const [albums, setAlbums] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchInProgress, setSearchInProgress] = useState(false);
	const [reorderMode, setReorderMode] = useState(false);
	const [reorderList, setReorderList] = useState([]);
	const [reorderSaving, setReorderSaving] = useState(false);
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [selectedImageForInfo, setSelectedImageForInfo] = useState(null);
	const infoHoverTimerRef = React.useRef(null);

	const clearInfoHoverTimer = () => {
		if (infoHoverTimerRef.current) {
			clearTimeout(infoHoverTimerRef.current);
			infoHoverTimerRef.current = null;
		}
	};
	// Short delay so passing the pointer over the button doesn't open the dialog
	const openInfoOnHover = (image) => {
		clearInfoHoverTimer();
		infoHoverTimerRef.current = setTimeout(() => setSelectedImageForInfo(image), 300);
	};
	// Grace period so the pointer can travel from the button to the dialog
	const closeInfoOnLeave = () => {
		clearInfoHoverTimer();
		infoHoverTimerRef.current = setTimeout(() => setSelectedImageForInfo(null), 500);
	};

	const savePositionsToDatabase = async (imageKey, positions) => {
		try {
			const token = localStorage.getItem('token');
			
			// Construct file path: gallery/album/image
			const galleryFolderName = props.album;
			const albumFolderName = props.params.album || "Pictures";
			
			// Handle imageKey - may start with / or not
			let cleanImageKey = imageKey;
			if (cleanImageKey.startsWith('/')) {
				cleanImageKey = cleanImageKey.substring(1);
			}
			
			const filePath = `${galleryFolderName}/${albumFolderName}/${cleanImageKey}`;
			
			// Use imageKey as the identifier for the image position
			const payload = {
				imageFilePath: filePath,
				positions: positions.map(pos => ({
					id: pos.id,
					name: pos.name,
					alias: pos.alias || null
				}))
			};

			await axios.post(
				`${constants.DATA1_API_URL}/gallery/image-positions`,
				payload,
				{
					headers: {
						'Authorization': token
					}
				}
			);

			console.log(`✓ Positions saved to database for image ${imageKey}:`, positions);
			
			// Refresh positions from database
			await fetchImagePositions(imageKey);
			
			return true;
		} catch (error) {
			console.error('Error saving positions to database:', error.message, error.response?.data);
			alert('Failed to save positions: ' + (error.response?.data?.message || error.message));
			return false;
		}
	};

	const fetchImagePositions = async (imageKey) => {
		try {
			const token = localStorage.getItem('token');
			
			// Construct file path: gallery/album/image
			const galleryFolderName = props.album;
			const albumFolderName = props.params.album || "Pictures";
			
			// Handle imageKey - may start with / or not
			let cleanImageKey = imageKey;
			if (cleanImageKey.startsWith('/')) {
				cleanImageKey = cleanImageKey.substring(1);
			}
			
			const filePath = `${galleryFolderName}/${albumFolderName}/${cleanImageKey}`;
			
			// Encode file path for URL
			const encodedFilePath = encodeURIComponent(filePath);

			const response = await axios.get(
				`${constants.DATA1_API_URL}/gallery/image-positions/${encodedFilePath}`,
				{
					headers: {
						'Authorization': token
					}
				}
			);

			if (response.data && response.data.positions) {
				setImagePositions(prev => ({
					...prev,
					[imageKey]: response.data.positions
				}));
				console.log(`✓ Loaded ${response.data.positions.length} positions for image ${imageKey}`);
			}
		} catch (error) {
			// Silently fail if positions don't exist yet
			if (error.response?.status !== 404) {
				console.error('Error fetching image positions:', error.message);
			}
		}
	};

	useEffect(() => {
		let lightbox = new PhotoSwipeLightbox({
			gallery: "#" + props.galleryID,
			children: "a",
			pswpModule: () => import("photoswipe"),
			counter: true,
			bgOpacity: 0.9,
			spacing: 0.5,
			wheelToZoom: true,
			zoom: true,
		});
		lightbox.init();
		lightboxRef.current = lightbox;

		// Track which image is in fullscreen so the admin panel can show its info and delete it
		lightbox.on('change', () => {
			const element = lightbox.pswp?.currSlide?.data?.element;
			if (!element) return;
			setFullscreenImage({
				key: element.getAttribute('data-image-key'),
				src: element.getAttribute('href')
			});
		});
		lightbox.on('close', () => setFullscreenImage(null));

		return () => {
			lightboxRef.current = null;
			lightbox.destroy();
			lightbox = null;
		};
	}, [props.galleryID]);

	// Check if user is admin
	useEffect(() => {
		const checkAdminStatus = async () => {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					setIsAdmin(false);
					return;
				}

				const response = await axios.get(
					`${constants.USER_API_URL}/auth/authenticated`,
					{
						headers: {
							'Authorization': token
						}
					}
				);

				if (response.data && response.data.perm) {
					const isUserAdmin = response.data.perm.some(p => p.role && p.role.id <= 2);
					setIsAdmin(isUserAdmin);
				}
			} catch (error) {
				console.error('Error checking admin status:', error);
				setIsAdmin(false);
			}
		};

		checkAdminStatus();
	}, []);

	const toggleImageSelection = (imageName) => {
		const newSelected = new Set(selectedImages);
		if (newSelected.has(imageName)) {
			newSelected.delete(imageName);
		} else {
			newSelected.add(imageName);
		}
		setSelectedImages(newSelected);
	};

	const toggleSelectAll = () => {
		if (selectedImages.size === displayedImages.length) {
			setSelectedImages(new Set());
		} else {
			const allImageNames = new Set(displayedImages.map(img => img.key.split('/').pop()));
			setSelectedImages(allImageNames);
		}
	};

	const clearSelection = () => {
		setSelectedImages(new Set());
		setMultiSelectMode(false);
	};

	const handleGridMouseDown = (e) => {
		if (!multiSelectMode || reorderMode || e.button !== 0) return;
		if (e.target.closest('button')) return;

		// Stops native image/link dragging so the marquee gets the mouse moves
		e.preventDefault();

		const startX = e.clientX;
		const startY = e.clientY;
		const baseSelection = (e.ctrlKey || e.metaKey || e.shiftKey) ? new Set(selectedImages) : new Set();
		let started = false;
		let box = null;

		const onMove = (moveEvent) => {
			const x = moveEvent.clientX;
			const y = moveEvent.clientY;
			if (!started) {
				if (Math.abs(x - startX) < 5 && Math.abs(y - startY) < 5) return;
				started = true;
				box = document.createElement('div');
				Object.assign(box.style, {
					position: 'fixed',
					border: '1px dashed #667eea',
					background: 'rgba(102, 126, 234, 0.15)',
					pointerEvents: 'none',
					zIndex: '998'
				});
				document.body.appendChild(box);
			}
			const left = Math.min(startX, x);
			const top = Math.min(startY, y);
			const right = Math.max(startX, x);
			const bottom = Math.max(startY, y);
			Object.assign(box.style, { left: left + 'px', top: top + 'px', width: (right - left) + 'px', height: (bottom - top) + 'px' });

			const next = new Set(baseSelection);
			gridRef.current?.querySelectorAll('[data-select-key]').forEach((el) => {
				const r = el.getBoundingClientRect();
				if (r.left < right && r.right > left && r.top < bottom && r.bottom > top) {
					next.add(el.dataset.selectKey);
				}
			});
			setSelectedImages(next);
		};

		const onUp = () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			if (box) box.remove();
			if (started) {
				// The browser fires click right after mouseup; swallow it so the tile under the cursor isn't toggled or opened
				suppressClickRef.current = true;
				setTimeout(() => { suppressClickRef.current = false; }, 0);
			}
		};

		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
	};

	const handleGridClickCapture = (e) => {
		if (suppressClickRef.current) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const canReorder = isAdmin && !isSearching && !!props.params.album && props.params.album !== 'Pictures';

	useEffect(() => {
		setReorderMode(false);
		setReorderList([]);
	}, [props.params.album]);

	const startReorder = () => {
		// preview.webp is the album cover, not an ordered image; the reorder API excludes it too
		setReorderList(pics.contents
			.map(img => ({
				key: img.key,
				src: img.src,
				name: img.key.split('/').pop()
			}))
			.filter(image => image.name !== 'preview.webp'));
		setReorderMode(true);
	};

	const cancelReorder = () => {
		setReorderMode(false);
		setReorderList([]);
	};

	const moveReorderItem = (fromIndex, toIndex) => {
		if (fromIndex === null || fromIndex === toIndex) return;
		setReorderList(prev => {
			const next = [...prev];
			const [moved] = next.splice(fromIndex, 1);
			next.splice(toIndex, 0, moved);
			return next;
		});
	};

	const saveReorder = async () => {
		setReorderSaving(true);
		try {
			const token = localStorage.getItem('token');
			await axios.post(
				`${constants.DATA1_API_URL}/gallery/reorder-images`,
				{
					galleryFolderName: props.album,
					albumName: props.params.album,
					orderedFileNames: reorderList.map(image => image.name)
				},
				{
					headers: {
						'Authorization': token,
						'Content-Type': 'application/json'
					}
				}
			);
			setReorderMode(false);
			setReorderList([]);
			await refreshGallery();
		} catch (error) {
			alert('Failed to save order: ' + (error.response?.data?.error || error.message));
		} finally {
			setReorderSaving(false);
		}
	};

	const deleteImageFromAlbum = async (image) => {
		const fileName = image.key.split('/').pop();
		const albumName = props.params.album || "Pictures";
		if (!window.confirm(`Permanently delete "${fileName}"? The file is removed from disk and this cannot be undone.`)) return false;

		try {
			const token = localStorage.getItem('token');
			const dbFilePath = `${props.album}/${albumName}/${fileName}`;
			await axios.delete(
				`${constants.DATA1_API_URL}/gallery/image/${encodeURIComponent(dbFilePath)}`,
				{ headers: { 'Authorization': token } }
			);

			if (selectedImageForInfo?.key === image.key) {
				clearInfoHoverTimer();
				setSelectedImageForInfo(null);
			}
			setSearchResults(prev => prev.filter(item => item.key !== image.key));
			await refreshGallery();
			return true;
		} catch (error) {
			alert('Failed to delete image: ' + (error.response?.data?.error || error.message));
			return false;
		}
	};

	const deleteFullscreenImage = async () => {
		const deleted = await deleteImageFromAlbum(fullscreenImage);
		if (deleted) {
			lightboxRef.current?.pswp?.close();
		}
	};

	const setImageAsPreview = async (imageName) => {
		try {
			const token = localStorage.getItem('token');
			const tokenSliced = token.slice(7); // Remove "Bearer " prefix
			const galleryId = props.album;
			const albumName = props.params.album || "Pictures";

			// Call contentAPI to set preview
			let contentApiBaseUrl = constants.CONTENT_DOMAIN;
			if (contentApiBaseUrl && contentApiBaseUrl.includes('/download')) {
				contentApiBaseUrl = contentApiBaseUrl.split('/download')[0];
			} else if (!contentApiBaseUrl) {
				contentApiBaseUrl = 'http://localhost:444';
			}

			const response = await axios.post(
				`${contentApiBaseUrl}files/gallery/${encodeURIComponent(galleryId)}/set-preview?token=${encodeURIComponent(tokenSliced)}`,
				{
					albumName: albumName,
					imageName: imageName
				}
			);

			if (response.data?.status) {
				console.log('✓ Image set as preview:', imageName);
				alert(`✓ Preview image set for "${albumName}"`);
				// Optional: refresh to show the preview.webp file
				// refreshGallery();
			} else {
				alert('Failed to set preview: ' + (response.data?.message || 'Unknown error'));
			}
		} catch (error) {
			console.error('Error setting preview:', error);
			alert('Failed to set preview: ' + (error.response?.data?.message || error.message));
		}
	};

	const refreshGallery = async () => {
		// Refetch the gallery data from contentAPI directory endpoint
		try {
			const token = localStorage.getItem('token');
			const galleryId = props.album;
			const tokenSliced = token.slice(7); // Remove "Bearer " prefix
			
			// Construct contentAPI URL from CONTENT_DOMAIN or use direct localhost
			// Format: http://localhost:444/files/directory/galleries/{galleryId}
			let contentApiBaseUrl = constants.CONTENT_DOMAIN;
			if (contentApiBaseUrl && contentApiBaseUrl.includes('/download')) {
				// Remove /download/ part to get base URL
				contentApiBaseUrl = contentApiBaseUrl.split('/download')[0];
			} else if (!contentApiBaseUrl) {
				// Fallback to direct localhost URL
				contentApiBaseUrl = 'http://localhost:444';
			}
			
			const directoryUrl = `${contentApiBaseUrl}files/directory/galleries/${encodeURIComponent(galleryId)}?token=${encodeURIComponent(tokenSliced)}`;
			console.log('Fetching from:', directoryUrl);
			
			const response = await axios.get(directoryUrl);
			
			if (response.data) {
				// Handle both old (flat array) and new (structured) API response formats
				let picturesContents = [];
				let children = {};
				const albumsList = [];
				
				if (Array.isArray(response.data)) {
					// OLD FORMAT: Flat array of { key: "/GalleryName/image.jpg" or "/GalleryName/album/image.jpg", size: 123, modified: 456 }
					console.log('Detected old API format (flat array)');
					const pathMap = {};
					
					response.data.forEach(fileObj => {
						const key = fileObj.key; // e.g., "/Angela Doll/image.jpg" or "/Angela Doll/Album/image.jpg"
						const pathParts = key.replace(/^\//, '').split('/'); // Remove leading /, split by /
						
						// pathParts[0] = gallery name, pathParts[1+] = path within gallery
						if (pathParts.length === 2) {
							// Root level file (Pictures album): /GalleryName/image.jpg
							const fileName = pathParts[1];
							picturesContents.push({
								isFile: true,
								key: `${galleryId}/${fileName}`,
								src: `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${galleryId}/${encodeURIComponent(fileName)}?token=${encodeURIComponent(tokenSliced)}`
							});
						} else if (pathParts.length > 2) {
							// Album subfolder: /GalleryName/AlbumName/ImageFile or /GalleryName/AlbumName/SubFolder/ImageFile
							const albumName = pathParts[1];
							const fileName = pathParts.slice(2).join('/'); // Everything after album name
							
							if (!pathMap[albumName]) {
								pathMap[albumName] = [];
							}
							pathMap[albumName].push({
								isFile: true,
								key: `${galleryId}/${albumName}/${fileName}`,
								src: `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${galleryId}/${encodeURIComponent(albumName)}/${encodeURIComponent(fileName)}?token=${encodeURIComponent(tokenSliced)}`
							});
						}
					});
					
					// Convert pathMap to children structure
					Object.entries(pathMap).forEach(([albumName, files]) => {
						children[albumName] = { contents: files };
						albumsList.push({
							folder_name: albumName,
							image_count: files.length
						});
					});
				} else {
					// NEW FORMAT: { files: [...], children: { album: { files: [...] } } }
					console.log('Detected new API format (structured)');
					const directoryData = response.data;
					
					if (directoryData.files && Array.isArray(directoryData.files)) {
						picturesContents = directoryData.files.map(file => ({
							isFile: true,
							key: `${galleryId}/${file}`,
							src: `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${galleryId}/${encodeURIComponent(file)}?token=${encodeURIComponent(tokenSliced)}`
						}));
					}
					
					if (directoryData.children && typeof directoryData.children === 'object') {
						Object.entries(directoryData.children).forEach(([albumName, albumData]) => {
							const fileCount = (albumData.files && Array.isArray(albumData.files)) ? albumData.files.length : 0;
							
							children[albumName] = {
								contents: (albumData.files || []).map(file => ({
									isFile: true,
									key: `${galleryId}/${albumName}/${file}`,
									src: `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${galleryId}/${encodeURIComponent(albumName)}/${encodeURIComponent(file)}?token=${encodeURIComponent(tokenSliced)}`
								}))
							};
							
							albumsList.push({
								folder_name: albumName,
								image_count: fileCount
							});
						});
					}
				}
				
				// Update full gallery data
				const updatedGalleryData = {
					contents: picturesContents,
					children: children
				};
				setGalleryData(updatedGalleryData);
				setAlbums(albumsList);
				
				// Update current album view based on which album is being displayed
				const currentAlbum = props.params.album || "Pictures";
				console.log('Current album being viewed:', currentAlbum);
				console.log('Available albums:', Object.keys(children));
				
				if (currentAlbum === "Pictures") {
					console.log('Displaying Pictures album with', picturesContents.length, 'images');
					setPics(updatedGalleryData);
				} else {
					const albumData = children[currentAlbum];
					console.log('Looking for album:', currentAlbum, '-> found:', !!albumData, 'with', albumData?.contents?.length || 0, 'images');
					if (albumData) {
						setPics(albumData);
					} else {
						// Album doesn't exist, show empty
						console.warn('Album not found:', currentAlbum);
						setPics({ contents: [], children: {} });
					}
				}
				
				setReadyToRender(true);
				
				console.log('✓ Gallery refreshed from contentAPI with', picturesContents.length, 'images in Pictures');
				console.log('✓ Albums refreshed:', albumsList.length, 'albums');
				console.log('✓ Sidebar should update with', Object.keys(children).length, 'albums');
			}
		} catch (error) {
			console.error('Error refreshing gallery:', error);
			alert('Failed to refresh gallery: ' + (error.message || 'Unknown error'));
			// Don't clear data - keep existing gallery state on error
		}
	};

	const performSearch = async (query) => {
		if (!query || query.trim().length === 0) {
			setSearchResults([]);
			setIsSearching(false);
			return;
		}

		trackSearch(query);
		try {
			setSearchInProgress(true);
			const galleryId = props.album;
			const tokenSliced = localStorage.getItem('token').slice(7);
			
			const response = await axios.get(
				`${constants.DATA1_API_URL}/gallery/${galleryId}/search?q=${encodeURIComponent(query)}`,
				{
					headers: {
						'Authorization': localStorage.getItem('token')
					}
				}
			);

			if (response.data && response.data.results) {
				// Transform results to display format
				const transformedResults = response.data.results.map(result => {
					// Build URL using gallery_folder_name, album_folder_name, and filename
					const galleryName = result.gallery_folder_name;
					const albumName = result.album_folder_name;
					const filename = result.filename;
					
					return {
						...result,
						isFile: true,
						key: `${galleryName}/${albumName}/${filename}`,
						src: `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${encodeURIComponent(galleryName)}/${encodeURIComponent(albumName)}/${encodeURIComponent(filename)}?token=${encodeURIComponent(tokenSliced)}`
					};
				});

				setSearchResults(transformedResults);
				setIsSearching(true);
				console.log(`✓ Found ${transformedResults.length} images matching "${query}"`);
			}
		} catch (error) {
			console.error('Error searching gallery:', error);
			alert('Search failed: ' + (error.response?.data?.error || error.message));
		} finally {
			setSearchInProgress(false);
		}
	};

	const clearSearch = () => {
		setSearchQuery("");
		setSearchResults([]);
		setIsSearching(false);
	};

	useEffect(() => {
		if (!props.images) {
			setPics({ contents: [], children: {} });
			setGalleryData({ contents: [], children: {} });
			setReadyToRender(true);
			return;
		}

		if (props.params.album == "Pictures" || props.params.album == null) {
			setPics(props.images);
			setGalleryData(props.images);
			setReadyToRender(true);
		}
		else {
			const albumData = props.images.children?.[props.params.album];
			if (albumData) {
				// Album has either 'contents' (from props) or 'files' (from API)
				const picsData = {
					contents: albumData.contents || [],
					children: {}
				};
				setPics(picsData);
			} else {
				// Album doesn't exist, show empty
				setPics({ contents: [], children: {} });
			}
			setGalleryData(props.images);
			setReadyToRender(true);
		}
		
		// Initialize albums from props.images.children if not already set from refresh
		if (albums.length === 0 && props.images?.children) {
			const initialAlbums = Object.entries(props.images.children).map(([albumName, albumData]) => ({
				folder_name: albumName,
				image_count: albumData?.contents?.length || albumData?.files?.length || 0
			}));
			setAlbums(initialAlbums);
		}
		
		// Update page title when gallery or album changes
		const galleryName = props.album || 'Gallery';
		const albumName = props.params.album || 'Pictures';
		document.title = `${galleryName} - ${albumName}`;
	}, [props.params, props.images, props.album])

	// Initialize displayed images when pics change
	useEffect(() => {
		console.log('pics.contents changed:', pics.contents?.length || 0, 'images');
		console.log('readyToRender:', readyToRender);
		if (readyToRender && pics.contents) {
			const initialImages = pics.contents.slice(0, imagesPerPage);
			console.log('Setting displayed images to', initialImages.length, 'images');
			setDisplayedImages(initialImages);
			setLoadedCount(imagesPerPage);
		} else {
			console.log('Skipping - readyToRender:', readyToRender, 'pics.contents exists:', !!pics.contents);
		}
	}, [readyToRender, pics.contents, imagesPerPage]);

	// Setup intersection observer for lazy loading
	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && readyToRender && pics.contents) {
					const nextCount = Math.min(loadedCount + imagesPerPage, pics.contents.length);
					if (nextCount > loadedCount) {
						const nextImages = pics.contents.slice(0, nextCount);
						setDisplayedImages(nextImages);
						setLoadedCount(nextCount);
					}
				}
			},
			{ threshold: 0.1 }
		);

		if (sentinelRef.current) {
			observer.observe(sentinelRef.current);
		}

		return () => {
			if (sentinelRef.current) {
				observer.unobserve(sentinelRef.current);
			}
		};
	}, [readyToRender, pics.contents, loadedCount, imagesPerPage]);


	return (
		<>
			<Container fluid style={{ padding: '20px' }}>
				<Row>
					{props.displayAlbum && (
						<Col
							lg={2}
							md={3}
							xs={12}
							style={{
								paddingRight: '10px',
								marginBottom: '20px',
								position: 'relative',
								...(isSidebarBesideGallery && { flex: `0 0 ${sidebarWidth}px`, maxWidth: `${sidebarWidth}px` })
							}}
						>
							<AlbumSidebar
								album={props.album}
								currentAlbum={props.params.album}
								images={galleryData}
								albums={albums.length > 0 ? albums : null}
							/>
							{isSidebarBesideGallery && (
								<div
									title="Drag to resize"
									onMouseDown={startSidebarResize}
									style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '10px', cursor: 'col-resize', zIndex: 2 }}
								/>
							)}
						</Col>
					)}
					<Col
						lg={props.displayAlbum ? 10 : 12}
						md={props.displayAlbum ? 9 : 12}
						xs={12}
						style={isSidebarBesideGallery ? { flex: '1 1 0', maxWidth: 'none', minWidth: 0 } : undefined}
					>
						<div style={{ marginBottom: '20px' }}>
							<h3>{props.params.album || 'Pictures'}</h3>
							<p style={{ color: '#666', fontSize: '14px' }}/>
						{readyToRender ? `${isSearching && searchResults.length > 0 ? searchResults.length : pics.contents?.length || 0} images` : 'Loading...'}
							{/* Search Bar */}
							<div style={{ display: 'flex', gap: '8px', marginBottom: '15px', alignItems: 'center' }}>
								<input
									type="text"
									placeholder="Search across all albums (by model, tag, position...)"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === 'Enter') {
											performSearch(searchQuery);
										}
									}}
									style={{
										flex: 1,
										padding: '8px 12px',
										border: '1px solid #ddd',
										borderRadius: '4px',
										fontSize: '14px'
									}}
								/>
								<button
									onClick={() => performSearch(searchQuery)}
									disabled={searchInProgress}
									style={{
										padding: '8px 16px',
										backgroundColor: searchInProgress ? '#95a5a6' : '#3498db',
										color: 'white',
										border: 'none',
										borderRadius: '4px',
										cursor: searchInProgress ? 'not-allowed' : 'pointer',
										fontSize: '12px',
										fontWeight: '600'
									}}
								>
									{searchInProgress ? '⏳ Searching...' : '🔍 Search'}
								</button>
								{isSearching && (
									<button
										onClick={clearSearch}
										style={{
											padding: '8px 16px',
											backgroundColor: '#e74c3c',
											color: 'white',
											border: 'none',
											borderRadius: '4px',
											cursor: 'pointer',
											fontSize: '12px',
											fontWeight: '600'
										}}
									>
										✕ Clear
									</button>
								)}
							</div>

{isSearching && searchResults.length > 0 && (
							<div style={{ 
								padding: '12px', 
								backgroundColor: '#e3f2fd', 
								borderRadius: '4px', 
								marginBottom: '15px',
								fontSize: '13px',
								color: '#1565c0'
							}}>
								Search results for "<strong>{searchQuery}</strong>" ({searchResults.length} images found)
							</div>
						)}
						{isSearching && searchResults.length === 0 && (
							<div style={{ 
								padding: '12px', 
								backgroundColor: '#fff3cd', 
								borderRadius: '4px', 
								marginBottom: '15px',
								fontSize: '13px',
								color: '#856404'
							}}>
								No results for "<strong>{searchQuery}</strong>" - Showing all album images
								</div>
							)}

							{isAdmin && (
								<div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
									{reorderMode && (
										<>
											<span style={{ color: '#666', fontSize: '13px', padding: '6px 12px' }}>
												Drag images to reorder, then save
											</span>
											<button
												onClick={saveReorder}
												disabled={reorderSaving}
												style={{
													padding: '6px 12px',
													backgroundColor: '#27ae60',
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													cursor: reorderSaving ? 'not-allowed' : 'pointer',
													fontSize: '12px',
													fontWeight: '600'
												}}
											>
												{reorderSaving ? 'Saving...' : 'Save Order'}
											</button>
											<button
												onClick={cancelReorder}
												disabled={reorderSaving}
												style={{
													padding: '6px 12px',
													backgroundColor: '#e74c3c',
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													cursor: reorderSaving ? 'not-allowed' : 'pointer',
													fontSize: '12px',
													fontWeight: '600'
												}}
											>
												Cancel
											</button>
										</>
									)}
									{selectedImages.size > 0 && (
										<>
											<button
												onClick={toggleSelectAll}
												style={{
													padding: '6px 12px',
													backgroundColor: selectedImages.size === displayedImages.length ? '#667eea' : '#7f8c8d',
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													cursor: 'pointer',
													fontSize: '12px',
													fontWeight: '600'
												}}
											>
												{selectedImages.size === displayedImages.length ? 'Deselect All' : 'Select All'}
											</button>
											<span style={{ color: '#666', fontSize: '13px', fontWeight: '600', padding: '6px 12px' }}>
												{selectedImages.size} selected
											</span>
											<MoveImageModal
												galleryId={props.album}
												currentAlbumId={props.params.album || "Pictures"}
												selectedImages={Array.from(selectedImages)}
												isMultiSelect={true}
												onSave={refreshGallery}
											/>
											<button
												onClick={clearSelection}
												style={{
													padding: '6px 12px',
													backgroundColor: '#e74c3c',
													color: 'white',
													border: 'none',
													borderRadius: '4px',
													cursor: 'pointer',
													fontSize: '12px',
													fontWeight: '600'
												}}
											>
												Clear Selection
											</button>
										</>
									)}
								</div>
							)}
						</div>
						
						<div
							className="pswp-gallery"
							id={props.galleryID}
							ref={gridRef}
							onMouseDown={handleGridMouseDown}
							onClickCapture={handleGridClickCapture}
						>

							{reorderMode ? (
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
									{reorderList.map((image, index) => (
										<div
											key={image.key}
											draggable
											onDragStart={(e) => {
												e.dataTransfer.setData('text/plain', String(index));
												setDraggedIndex(index);
											}}
											onDragOver={(e) => e.preventDefault()}
											onDrop={(e) => {
												e.preventDefault();
												moveReorderItem(draggedIndex, index);
												setDraggedIndex(null);
											}}
											onDragEnd={() => setDraggedIndex(null)}
											style={{
												position: 'relative',
												width: '150px',
												cursor: 'grab',
												border: draggedIndex === index ? '2px dashed #667eea' : '2px solid transparent',
												borderRadius: '4px',
												opacity: draggedIndex === index ? 0.5 : 1
											}}
										>
											<img
												src={image.src}
												alt={image.name}
												loading="lazy"
												draggable={false}
												style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '4px', display: 'block' }}
											/>
											<div style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: '4px', padding: '2px 6px', fontSize: '12px', fontWeight: '600' }}>
												{index + 1}
											</div>
											<div style={{ fontSize: '11px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{image.name}
											</div>
										</div>
									))}
								</div>
							) : readyToRender ? (
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
								{(isSearching && searchResults.length > 0 ? searchResults : displayedImages).map((image) => (
										<div 
											key={props.galleryID + '-' + image.key} 
											data-select-key={image.key.split('/').pop()}
											style={{ position: 'relative', display: 'inline-block' }}
											onMouseEnter={() => setHoveredImageKey(image.key)}
											onMouseLeave={() => { setHoveredImageKey(null); setOpenMenuKey(null); }}
										>
											{image.isFile ? (
												<>
													<a
														href={image.src}
														data-image-key={image.key}
														data-pswp-width={image.width}
														data-pswp-height={image.height}
														target="_blank"
														rel="noreferrer"
													>
														<img
															src={image.src}
															alt="image"
															width={200}
															style={{
																borderRadius: "5px",
																transition: 'transform 0.2s',
																cursor: 'pointer'
															}}
															onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
															onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
														/>
													</a>
													
													{/* Search result metadata tooltip */}
													{isSearching && hoveredImageKey === image.key && (
														<div style={{
															position: 'absolute',
															bottom: '-180px',
															left: '0',
															backgroundColor: '#fff',
															border: '1px solid #ddd',
															borderRadius: '4px',
															padding: '8px',
															width: '220px',
															fontSize: '11px',
															zIndex: 100,
															boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
															color: '#333'
														}}>
															<div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{image.album_title || image.album_folder_name}</div>
															{image.album_scenes && (
																<div><strong>🎬 Scenes:</strong> {image.album_scenes}</div>
															)}
															{image.album_models && (
																<div><strong>Album Models:</strong> {image.album_models}</div>
															)}
															{image.album_tags && (
																<div><strong>Album Tags:</strong> {image.album_tags}</div>
															)}
															{image.album_positions && (
																<div><strong>Album Positions:</strong> {image.album_positions}</div>
															)}
															{image.models && (
																<div><strong>Image Models:</strong> {image.models}</div>
															)}
															{image.tags && (
																<div><strong>Image Tags:</strong> {image.tags}</div>
															)}
															{image.positions && (
																<div><strong>Positions:</strong> {image.positions}</div>
															)}
														</div>
													)}
													
													{multiSelectMode && (
														<div style={{
															position: 'absolute',
															top: '8px',
															left: '8px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															width: '24px',
															height: '24px',
															backgroundColor: selectedImages.has(image.key.split('/').pop()) ? '#667eea' : 'white',
															border: '2px solid #667eea',
															borderRadius: '4px',
															cursor: 'pointer',
															zIndex: 10
														}}
														onClick={() => toggleImageSelection(image.key.split('/').pop())}
													>
														{selectedImages.has(image.key.split('/').pop()) && (
															<span style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>✓</span>
														)}
													</div>
													)}
													{hoveredImageKey === image.key && isAdmin && !multiSelectMode && (
														<button
															style={{
																position: 'absolute',
																top: '4px',
																left: '4px',
																background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
																color: 'white',
																border: 'none',
																borderRadius: '50%',
																width: '32px',
																height: '32px',
																cursor: 'pointer',
																fontSize: '16px',
																boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
																padding: '0'
															}}
															onClick={() => { clearInfoHoverTimer(); setSelectedImageForInfo(image); }}
																														onMouseEnter={() => openInfoOnHover(image)}
																														onMouseLeave={closeInfoOnLeave}
																														title="More info"
														>
															ℹ️
														</button>
													 )}
													{hoveredImageKey === image.key && isAdmin && !multiSelectMode && (
																											<div style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 15 }}>
																												<ToolbarMenuButton
																													type="button"
																													onClick={() => setOpenMenuKey(openMenuKey === image.key ? null : image.key)}
																													title="More actions"
																													aria-label="More actions"
																													aria-expanded={openMenuKey === image.key}
																												>
																													⋮
																												</ToolbarMenuButton>
																												{openMenuKey === image.key && (
																													<ToolbarMenuPanel>
																														<ToolbarMenuItem onClick={() => { setOpenMenuKey(null); setImageAsPreview(image.key.split('/').pop()); }}>
																															🖼️ Set as album preview
																														</ToolbarMenuItem>
																														<ToolbarMenuItem onClick={() => { setOpenMenuKey(null); setSelectedImageForTagging(image); }}>
																															🏷️ Tag positions
																														</ToolbarMenuItem>
																														{typeof props.onSendToGarment === 'function' && (
																															<ToolbarMenuItem onClick={() => { setOpenMenuKey(null); props.onSendToGarment(image); }}>
																																👗 Send to Garment Manager
																															</ToolbarMenuItem>
																														)}
																														<ToolbarMenuItem onClick={() => { setOpenMenuKey(null); setMovingImageName(image.key.split('/').pop()); }}>
																															📁 Move to album
																														</ToolbarMenuItem>
																														{image.key.split('/').pop() !== 'preview.webp' && (
																															<ToolbarMenuItem $danger onClick={() => { setOpenMenuKey(null); deleteImageFromAlbum(image); }}>
																																🗑️ Delete image
																															</ToolbarMenuItem>
																														)}
																													</ToolbarMenuPanel>
																												)}
																											</div>
																										)}
												</>
											) : null}
										</div>
									))}
									{!isSearching && <div ref={sentinelRef} style={{ height: '50px', marginTop: '20px' }} />}
									{!isSearching && loadedCount < pics.contents?.length && (
										<div style={{ width: '100%', textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
											Scroll to load more images... ({loadedCount} of {pics.contents?.length})
										</div>
									)}
									{isSearching && searchResults.length === 0 && <div ref={sentinelRef} style={{ height: '50px', marginTop: '20px' }} />}
									{isSearching && searchResults.length === 0 && loadedCount < pics.contents?.length && (
										<div style={{ width: '100%', textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
											Scroll to load more images... ({loadedCount} of {pics.contents?.length})
										</div>
									)}
								</div>
							) : null}
						</div>
					</Col>
				</Row>
			</Container>

			<MoveImageModal
				hideTrigger
				open={movingImageName !== null}
				onClose={() => setMovingImageName(null)}
				galleryId={props.album}
				currentAlbumId={props.params.album || "Pictures"}
				imageName={movingImageName || ''}
				onSave={refreshGallery}
			/>

		{isAdmin && createPortal(
			<div style={{ position: 'fixed', right: '20px', bottom: '92px', zIndex: 997, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
				{!floatingCollapsed && (
					<>
				<FloatingAdminButton onClick={refreshGallery} title="Refresh gallery" aria-label="Refresh gallery">
					<RefreshIcon />
				</FloatingAdminButton>
				<FloatingAdminButton
					onClick={() => {
						if (multiSelectMode) {
							clearSelection();
						} else {
							setMultiSelectMode(true);
						}
					}}
					$active={multiSelectMode}
					title={multiSelectMode ? 'Multi-select on (click to turn off)' : 'Multi-select'}
					aria-label="Toggle multi-select"
					aria-pressed={multiSelectMode}
				>
					<DoneAllIcon />
				</FloatingAdminButton>
				<FloatingAdminButton
					onClick={() => setEditMode(prev => !prev)}
					$active={editMode}
					title="Edit mode: when on, fullscreen view shows image info and delete"
					aria-label="Toggle edit mode"
					aria-pressed={editMode}
				>
					<EditIcon />
				</FloatingAdminButton>
				{canReorder && !reorderMode && (
					<FloatingAdminButton onClick={startReorder} title="Reorder images" aria-label="Reorder images">
						<SwapVertIcon />
					</FloatingAdminButton>
				)}
					</>
				)}
				<FloatingAdminButton
					onClick={() => setFloatingCollapsed(prev => !prev)}
					title={floatingCollapsed ? 'Show admin tools' : 'Hide admin tools'}
					aria-label={floatingCollapsed ? 'Show admin tools' : 'Hide admin tools'}
					aria-expanded={!floatingCollapsed}
				>
					{floatingCollapsed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</FloatingAdminButton>
			</div>,
			document.body
		)}

		{fullscreenImage?.key && isAdmin && editMode && createPortal(
			<FullscreenAdminPanel
				galleryId={props.album}
				albumName={props.params.album || "Pictures"}
				fileName={fullscreenImage.key.split('/').pop()}
				open={fullscreenPanelOpen}
				onToggle={() => setFullscreenPanelOpen(prev => !prev)}
				onDelete={deleteFullscreenImage}
			/>,
			document.body
		)}

		{selectedImageForInfo && isAdmin && (
			<ImageInfoModal
				galleryId={props.album}
				albumName={props.params.album || "Pictures"}
				fileName={selectedImageForInfo.key.split('/').pop()}
				imageUrl={selectedImageForInfo.src}
				onClose={() => { clearInfoHoverTimer(); setSelectedImageForInfo(null); }}
				onHoverEnter={clearInfoHoverTimer}
				onHoverLeave={closeInfoOnLeave}
			/>
		)}

		{selectedImageForTagging && isAdmin && (<>
			<ImagePositionTagger
				imageKey={selectedImageForTagging.key}
				imageUrl={selectedImageForTagging.src}
				galleryFolderName={props.album}
				albumFolderName={props.params.album || "Pictures"}
				onClose={() => setSelectedImageForTagging(null)}
				onPositionsUpdate={async (imageKey, positions) => {
					// Save to local state first
					setImagePositions({
						...imagePositions,
						[imageKey]: positions
					});

					// Save to database
					await savePositionsToDatabase(imageKey, positions);
				}}
			/>
		</>)}
	</>
	);
}